import { GRADING_REQUIREMENTS, GRADE_ORDER, QUALIFICATIONS, GRADE_LABELS } from '../constants/grading';

// Borderline window: a "met" weeks/days requirement that sits within this
// fraction above its minimum is flagged borderline rather than a clean pass.
const BORDERLINE_MARGIN = 0.05;

// JIGS treats grade progression as additive: holding a prior grade credits
// the applicant for the weeks they had to log to achieve it. The new
// step-up requirement is a constant 200 weeks, regardless of discipline,
// applied when `current_grade` is at or above the level below the one
// being assessed.
const PER_STEP_WEEKS = 200;
const PRIOR_FOR_CREDIT = {
  technician: null,             // no prior — full minWeeks always required
  senior_technician: 'technician',
  supervisor: 'senior_technician',
};

// Quick id -> human name lookup for building reason strings.
const QUAL_NAME_BY_ID = QUALIFICATIONS.reduce((acc, q) => {
  acc[q.id] = q.name;
  return acc;
}, {});

/**
 * Defensively parse a value that may already be an object/array or may arrive
 * as a JSON string (e.g. straight off a Postgres JSON/text column). Returns the
 * supplied fallback on null/undefined or unparseable input.
 */
function parseMaybeJson(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed == null ? fallback : parsed;
    } catch {
      return fallback;
    }
  }
  return value;
}

/** Whole/decimal years from an ISO-ish date string to today. 0 if no date. */
function yearsSince(dateStr) {
  if (!dateStr) return 0;
  const start = new Date(dateStr);
  if (Number.isNaN(start.getTime())) return 0;
  const now = new Date();
  const ms = now.getTime() - start.getTime();
  if (ms <= 0) return 0;
  return ms / (365.25 * 24 * 60 * 60 * 1000);
}

/**
 * Compute a crew member's grading progress within a single discipline.
 *
 * Pure function — never mutates inputs, tolerant of empty/missing data and of
 * Postgres NUMERIC-as-string values (coerced via Number()). Returns a summary
 * suitable for driving a progression bar plus a per-grade eligibility list.
 *
 * @param {Array<Object>} entries  experience rows: { weeks_worked, odd_days, total_days }
 * @param {string} discipline      one of the discipline ids (defaults to 'physical')
 * @param {Object} profile         { date_entered_industry, current_grade,
 *                                    grade_applied_for, qualifications }
 *
 * signatureCount is the number of DISTINCT, non-empty reference_email values
 * across the experience entries whose approval_status === 'approved' (email
 * trimmed + lowercased for distinctness). Only approved references, deduplicated
 * by email, count toward the "Supervisor references — N/3" requirement.
 * @returns {{
 *   discipline: string,
 *   totalWeeks: number,
 *   totalDays: number,
 *   totalYears: number,
 *   signatureCount: number,
 *   currentBandIndex: number,
 *   nextGrade: (string|null),
 *   weeksToNext: (number|null),
 *   daysToNext: (number|null),
 *   eligibility: Array<{
 *     grade: string,
 *     status: string,
 *     reasons: string[],
 *     requirements: Array<{ key: string, label: string, detail?: string, met: (boolean|null) }>
 *   }>
 * }}
 */
export function computeGradingProgress(entries, discipline, profile, options) {
  const disc = discipline && GRADING_REQUIREMENTS[discipline] ? discipline : 'physical';
  const reqsByGrade = GRADING_REQUIREMENTS[disc];
  const safeProfile = profile || {};
  const opts = options && typeof options === 'object' ? options : {};
  // Whether the applicant has uploaded a passport-style photo. Required for
  // every grade — JIGS won't accept the application packet without one.
  const hasPhoto = !!opts.hasPhoto;

  const rows = Array.isArray(entries) ? entries : [];

  let totalWeeks = 0;
  let totalDays = 0;
  for (const e of rows) {
    if (!e) continue;
    const weeks = Number(e.weeks_worked) || 0;
    const oddDays = Number(e.odd_days) || 0;
    const totDays = Number(e.total_days) || 0;
    totalWeeks += weeks;
    // Prefer explicit total_days; fall back to weeks*5 + odd days when absent/0.
    totalDays += totDays > 0 ? totDays : weeks * 5 + oddDays;
  }

  const totalYears = yearsSince(safeProfile.date_entered_industry);

  // Supervisor references: count DISTINCT, non-empty reference_email values
  // across entries whose approval_status === 'approved' (trimmed + lowercased
  // for distinctness). Only approved references count, deduplicated by email;
  // entries lacking an email or not exactly 'approved' are skipped.
  const distinctApprovedEmails = new Set();
  for (const e of rows) {
    if (!e) continue;
    if (e.approval_status !== 'approved') continue;
    const email = typeof e.reference_email === 'string' ? e.reference_email.trim() : '';
    if (email !== '') distinctApprovedEmails.add(email.toLowerCase());
  }
  const signatureCount = distinctApprovedEmails.size;

  // Qualification YES/NO flag map.
  const quals = parseMaybeJson(safeProfile.qualifications, {});
  const qualFlags = quals && typeof quals === 'object' && !Array.isArray(quals) ? quals : {};

  // Current grade ranking for progressive-grade checks AND for the
  // progression bar's "re-baseline" behaviour (the bar shows segments
  // below the held grade as faded "presumed" green).
  const currentGradeIndex = GRADE_ORDER.indexOf(safeProfile.current_grade);
  const heldGrade =
    currentGradeIndex > 0 && currentGradeIndex < GRADE_ORDER.length
      ? GRADE_ORDER[currentGradeIndex]
      : null;
  const presumedMinWeeks = heldGrade ? Number(reqsByGrade[heldGrade]?.minWeeks) || 0 : 0;
  // Logged weeks stack ON TOP of the presumed floor: a Senior with 100wk
  // logged effectively sits at 400 + 100 = 500wk on the cumulative scale.
  const effectiveWeeks = presumedMinWeeks + totalWeeks;

  // currentBandIndex: highest grade whose minWeeks is met by effective weeks.
  // Trainee (index 0) is always satisfied.
  let currentBandIndex = 0;
  for (let i = 1; i < GRADE_ORDER.length; i++) {
    const grade = GRADE_ORDER[i];
    const req = reqsByGrade[grade];
    if (req && effectiveWeeks >= req.minWeeks) {
      currentBandIndex = i;
    } else {
      break;
    }
  }

  const nextGrade =
    currentBandIndex < GRADE_ORDER.length - 1 ? GRADE_ORDER[currentBandIndex + 1] : null;
  let weeksToNext = null;
  let daysToNext = null;
  if (nextGrade) {
    const nextReq = reqsByGrade[nextGrade];
    weeksToNext = Math.max(0, nextReq.minWeeks - effectiveWeeks);
    daysToNext = Math.max(0, nextReq.minDays - totalDays);
  }

  const eligibility = [];
  for (let i = 1; i < GRADE_ORDER.length; i++) {
    const grade = GRADE_ORDER[i];
    const req = reqsByGrade[grade];
    if (!req) continue;

    const reasons = [];
    // Full per-grade checklist: EVERY requirement, met or not, so the UI can
    // render green ticks for completed items alongside the outstanding ones.
    // `met` is true | false | null (null = informational, cannot auto-verify).
    const requirements = [];
    let hardFail = false;
    let borderline = false;

    // Passport-style photo (hard) — required by JIGS at every grade.
    requirements.push({
      key: 'photo',
      label: 'Passport-style photo',
      met: hasPhoto,
    });
    if (!hasPhoto) {
      hardFail = true;
      reasons.push('Passport-style photo not uploaded');
    }

    // Years (hard).
    requirements.push({
      key: 'years',
      label: 'Years in industry',
      detail: `${totalYears.toFixed(1)} / ${req.minYears} yrs`,
      met: totalYears >= req.minYears,
    });
    if (totalYears < req.minYears) {
      hardFail = true;
      reasons.push(`Years: ${totalYears.toFixed(1)} / ${req.minYears}`);
    }

    // Weeks (hard, with borderline window when met).
    //
    // The three states this can resolve to:
    //   1. allCumulativeMet — totalWeeks satisfies the highest cumulative
    //      requirement (Supervisor's minWeeks). Independent proof beyond
    //      doubt → green tick, no warning, for every grade row.
    //   2. heldThisGrade — applicant already holds this grade (or above).
    //      Presumed via their current grading record → amber half-tick +
    //      warning telling them to bring proof of this grade.
    //   3. heldPriorForCredit + 200 weeks since — step-up met (200 weeks
    //      after the grade below). Green tick, no warning.
    //   Otherwise: cumulative check; cross if not met.
    var priorForCredit = PRIOR_FOR_CREDIT[grade];
    var thisGradeIndex = GRADE_ORDER.indexOf(grade);
    var heldThisGrade = thisGradeIndex >= 0 && currentGradeIndex >= thisGradeIndex;
    var heldPriorForCredit =
      !!priorForCredit && currentGradeIndex >= GRADE_ORDER.indexOf(priorForCredit);
    var topCumulative = reqsByGrade.supervisor ? reqsByGrade.supervisor.minWeeks : req.minWeeks;
    var allCumulativeMet = totalWeeks >= topCumulative;

    var weeksMet;
    var metViaPrior;
    var weeksNote;
    var detailThreshold;
    var detailText;

    if (allCumulativeMet) {
      // Independent proof — overrides everything.
      weeksMet = true;
      metViaPrior = false;
      weeksNote = null;
      detailThreshold = req.minWeeks;
      detailText = `${totalWeeks} / ${req.minWeeks} wk`;
    } else if (heldThisGrade) {
      // Presumed via current grade — proof of THIS grade required.
      weeksMet = true;
      metViaPrior = true;
      weeksNote =
        'Presumed via your current grade. ⚠️ Proof of ' + GRADE_LABELS[grade] +
        ' grading required — either on file with the JIGS committee, or uploaded as a Supporting Document.';
      detailThreshold = req.minWeeks;
      detailText = 'Presumed via current grade';
    } else if (heldPriorForCredit && totalWeeks >= PER_STEP_WEEKS) {
      // Step-up met — green, no warning. Detail shows the 200-week threshold.
      weeksMet = true;
      metViaPrior = false;
      weeksNote = null;
      detailThreshold = PER_STEP_WEEKS;
      detailText = `${totalWeeks} / ${PER_STEP_WEEKS} wk (since ${GRADE_LABELS[priorForCredit]})`;
    } else if (totalWeeks >= req.minWeeks) {
      // Cumulative met by logged work alone (no prior credit needed).
      weeksMet = true;
      metViaPrior = false;
      weeksNote = null;
      detailThreshold = req.minWeeks;
      detailText = `${totalWeeks} / ${req.minWeeks} wk`;
    } else {
      // Not met. If step-up is on the path, show that threshold; else cumulative.
      weeksMet = false;
      metViaPrior = false;
      weeksNote = null;
      if (heldPriorForCredit) {
        detailThreshold = PER_STEP_WEEKS;
        detailText = `${totalWeeks} / ${PER_STEP_WEEKS} wk (since ${GRADE_LABELS[priorForCredit]})`;
      } else {
        detailThreshold = req.minWeeks;
        detailText = `${totalWeeks} / ${req.minWeeks} wk`;
      }
    }

    requirements.push({
      key: 'weeks',
      label: 'Weeks worked (5-day)',
      detail: detailText,
      met: weeksMet,
      met_via_prior: metViaPrior,
      note: weeksNote,
    });
    if (!weeksMet) {
      hardFail = true;
      reasons.push(`Weeks: ${totalWeeks} / ${detailThreshold}`);
    } else if (
      !metViaPrior &&
      detailThreshold > 0 &&
      totalWeeks <= detailThreshold * (1 + BORDERLINE_MARGIN)
    ) {
      borderline = true;
      reasons.push(`Weeks borderline: ${totalWeeks} / ${detailThreshold}`);
    }

    // Days check intentionally omitted from the per-grade requirements list:
    // weeks already encodes the same thresholding (weeks × 5 ≈ days) and a
    // separate "Total days" row just creates noise + a second failure point
    // when an applicant has logged weeks but skipped odd days. totalDays is
    // still calculated and returned for any downstream consumer.

    // Supervisor signatures (hard).
    if (req.supervisorSignatures > 0) {
      requirements.push({
        key: 'signatures',
        label: 'Supervisor references',
        detail: `${signatureCount} / ${req.supervisorSignatures}`,
        met: signatureCount >= req.supervisorSignatures,
      });
    }
    if (signatureCount < req.supervisorSignatures) {
      hardFail = true;
      reasons.push(`Signatures: ${signatureCount} / ${req.supervisorSignatures}`);
    }

    // Qualifications (hard) — every required qualId must be flagged true.
    for (const qualId of req.qualifications) {
      const met = !!qualFlags[qualId];
      requirements.push({
        key: `qual:${qualId}`,
        label: QUAL_NAME_BY_ID[qualId] || qualId,
        met,
      });
      if (!met) {
        hardFail = true;
        reasons.push(`Missing qualification: ${QUAL_NAME_BY_ID[qualId] || qualId}`);
      }
    }

    // Progressive grade (hard) — applicant must already hold the prior grade.
    if (req.progressiveGradeRequired) {
      const requiredIdx = GRADE_ORDER.indexOf(req.progressiveGradeRequired);
      const label = GRADE_LABELS[req.progressiveGradeRequired] || req.progressiveGradeRequired;
      const met = currentGradeIndex >= requiredIdx;
      requirements.push({ key: 'progressive', label: `Holds ${label} grade`, met });
      if (!met) {
        hardFail = true;
        reasons.push(`Does not yet hold ${label}`);
      }
    }

    // Risk assessments — cannot be verified here. Manual note only, never fails.
    if (req.riskAssessments > 0) {
      requirements.push({
        key: 'risk_assessments',
        label: `${req.riskAssessments} risk assessments`,
        detail: 'verify manually',
        met: null,
      });
      reasons.push(`Needs ${req.riskAssessments} risk assessments (verify manually)`);
    }

    // Dual-discipline competence — cannot be verified here. Manual note only,
    // never fails (does not set hardFail or affect pass/borderline/fail status).
    if (req.dualDiscipline) {
      requirements.push({
        key: 'dual',
        label: 'Competence in a second discipline',
        detail: 'verify manually',
        met: null,
      });
      reasons.push('Requires competence in a second discipline (verify manually)');
    }

    let status;
    if (hardFail) status = 'fail';
    else if (borderline) status = 'borderline';
    else status = 'pass';

    eligibility.push({ grade, status, reasons, requirements });
  }

  return {
    discipline: disc,
    totalWeeks,
    totalDays,
    totalYears,
    signatureCount,
    currentBandIndex,
    nextGrade,
    weeksToNext,
    daysToNext,
    eligibility,
    currentGrade: heldGrade,
    presumedMinWeeks,
    effectiveWeeks,
  };
}
